import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ErrorState, Spinner } from '../components/ui.jsx';
import { useFetch, useTitle } from '../lib/hooks.js';
import { formatDate, formatDateTime, formatXaf, titleCase } from '../lib/format.js';
import { saveElementAsPdf } from '../lib/pdf.js';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';

/**
 * The receipt for a refund, for the landlord's records and for the person who was
 * refunded: what was handed back, how, for what, and anything kept back with the
 * reason. Laid out like a rent receipt so the two sit side by side in a file.
 */
const PrintableRefund = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { paymentMethods, platform } = useMeta();
  const { data, loading, error, reload } = useFetch(`/refunds/${id}`, [id]);
  const sheetRef = useRef(null);
  const [saving, setSaving] = useState(false);

  useTitle(data ? `Refund ${data.refund.refundNumber}` : 'Refund');

  useEffect(() => {
    document.body.classList.add('printing');
    return () => document.body.classList.remove('printing');
  }, []);

  const downloadPDF = async () => {
    if (!sheetRef.current || !data || saving) return;
    setSaving(true);
    try {
      await saveElementAsPdf(sheetRef.current, {
        margin: 10,
        filename: `refund-${data.refund.refundNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a5', orientation: 'portrait' }
      });
    } catch (cause) {
      console.error('Refund PDF failed:', cause);
      toast.error('Could not build the PDF. Use the Print button and save as PDF instead.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const { refund } = data;
  const deposit = refund.kind === 'deposit';
  const methodLabel = paymentMethods.find((method) => method.slug === refund.method)?.label || titleCase(refund.method);

  return (
    <div className="print-page">
      <div className="print-toolbar">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
          Back
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
          Print
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={downloadPDF} disabled={saving}>
          {saving ? 'Preparing PDF...' : '↓ Download PDF'}
        </button>
      </div>

      <article ref={sheetRef} className={`receipt-sheet ${refund.voided ? 'is-void' : ''}`}>
        {refund.voided ? <div className="void-stamp">Cancelled</div> : null}

        <header className="receipt-head">
          <div>
            <h1>{deposit ? 'Deposit refund' : 'Refund'}</h1>
            <p className="receipt-number">{refund.refundNumber}</p>
          </div>
          <div className="receipt-issuer">
            {refund.landlord.logoUrl ? <img className="doc-logo" src={refund.landlord.logoUrl} alt="" /> : null}
            <strong>{refund.landlord.name}</strong>
            {refund.landlord.phone ? <span>{refund.landlord.phone}</span> : null}
            <span className="muted">{platform?.name}</span>
          </div>
        </header>

        <table className="receipt-body">
          <tbody>
            <tr>
              <th scope="row">Refunded to</th>
              <td>
                <strong>{refund.recipient.name}</strong>
                {refund.recipient.phone ? <span className="muted"> {refund.recipient.phone}</span> : null}
              </td>
            </tr>
            <tr>
              <th scope="row">The sum of</th>
              <td>
                <strong className="receipt-amount">{formatXaf(refund.amount)}</strong>
                <span className="receipt-words">({refund.amountInWords})</span>
              </td>
            </tr>
            <tr>
              <th scope="row">Being</th>
              <td>
                {deposit ? 'Refund of the security deposit' : 'Refund of money paid over the bills'}
                {refund.for ? <span className="muted"> - {refund.for}</span> : null}
              </td>
            </tr>
            {refund.withheld ? (
              <tr>
                <th scope="row">Kept from the deposit</th>
                <td>
                  {formatXaf(refund.withheld)}
                  <span className="muted"> - {refund.withheldReason}</span>
                </td>
              </tr>
            ) : null}
            <tr>
              <th scope="row">Paid by</th>
              <td>
                {methodLabel}
                {refund.reference ? <span className="muted"> - reference {refund.reference}</span> : null}
              </td>
            </tr>
            <tr>
              <th scope="row">Date paid</th>
              <td>{formatDate(refund.paidOn)}</td>
            </tr>
            {refund.note ? (
              <tr>
                <th scope="row">Note</th>
                <td>{refund.note}</td>
              </tr>
            ) : null}
          </tbody>
        </table>

        <footer className="receipt-foot">
          <div className="receipt-sign">
            <span className="receipt-sign-line" />
            <span>Received by {refund.recipient.name}</span>
          </div>
          <div className="receipt-meta">
            <span>Recorded {formatDateTime(refund.createdAt)} by {refund.issuedBy}</span>
            <span className="muted">
              Every refund is written into the money log of {platform?.name || 'the platform'} and cannot be changed, only
              cancelled with a reason.
            </span>
          </div>
        </footer>

        {refund.proofUrl ? (
          <section className="refund-proof">
            <h2>Proof of payment</h2>
            {/\.pdf$/i.test(refund.proofUrl) ? (
              <a href={refund.proofUrl} target="_blank" rel="noreferrer" className="link-strong">
                Open the proof (PDF)
              </a>
            ) : (
              <a href={refund.proofUrl} target="_blank" rel="noreferrer">
                <img src={refund.proofUrl} alt="Proof of the refund payment" />
              </a>
            )}
          </section>
        ) : null}

        {refund.voided ? (
          <p className="receipt-void-note">
            This refund was cancelled on {formatDate(refund.voidedAt)}
            {refund.voidReason ? `: ${refund.voidReason}` : '.'}
          </p>
        ) : null}
      </article>
    </div>
  );
};

export default PrintableRefund;
